import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    ISelectedButtonsData,
    ITextEditorLinkAttachment,
    ITextEditorActiveButtons,
} from '@fi-shared/fi-text-editor/fi-text-editor';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { fromEvent } from 'rxjs';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { MatMenu } from '@angular/material/menu';
import { ITextPanelMenuItem } from '@fi-shared/fi-text-panel/simple-text-panel/text-panel-base';
import { LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { cloneDeep, findClosest } from '@fi-shared/utils';

interface ITextEditorPanelButton {
    onClick: ($event: MouseEvent) => void;
    isSelected?: () => boolean;
    iconClass: string;
    titleTextId: string;
    menuTriggerFor?: MatMenu;
    buttonClass?: string;
    condition?: () => boolean;
}

@Component({
    selector: 'fi-text-editor-panel',
    template: `
        @for (panelButton of panelButtons; track panelButton) {
            @if (!panelButton.condition || panelButton.condition()) {
                @if (panelButton.menuTriggerFor) {
                    <button
                        #menuTrigger="matMenuTrigger"
                        (click)="panelButton.onClick($event)"
                        [attr.class]="panelButton.buttonClass"
                        [attr.title]="panelButton.titleTextId | fiAppText"
                        [class.BrandedColor]="
                            menuTrigger.menuOpen || (panelButton.isSelected && panelButton.isSelected())
                        "
                        [matMenuTriggerFor]="panelButton.menuTriggerFor"
                    >
                        <span [ngClass]="panelButton.iconClass"></span>
                    </button>
                }
                @if (!panelButton.menuTriggerFor) {
                    <button
                        (click)="panelButton.onClick($event)"
                        [attr.class]="panelButton.buttonClass"
                        [attr.title]="panelButton.titleTextId | fiAppText"
                        [class.BrandedColor]="panelButton.isSelected && panelButton.isSelected()"
                    >
                        <span [ngClass]="panelButton.iconClass"></span>
                    </button>
                }
            }
        }

        @if (isGiphyOverlayShown$ | async) {
            <fi-insert-giphy (giphyItemClick)="onGiphyItemClick($event)" (onClose)="toggleGiphyOverlay($event)">
            </fi-insert-giphy>
        }

        <mat-menu #selectLinkToType class="fi-rounded-menu text-panel-menu">
            @for (linkToItem of availableLinkToItems; track linkToItem) {
                <button mat-menu-item (click)="onInsertLinkClick(linkToItem.value)">
                    <mat-icon [class]="linkToItem.iconClass"></mat-icon>
                    <span>{{ linkToItem.label | fiAppText }}</span>
                </button>
            }
        </mat-menu>
    `,
    styleUrls: ['./fi-text-editor-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-text-editor-panel-new' },
    standalone: false,
})
export class FiTextEditorPanelComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() inputElementRef: ElementRef<HTMLElement>;
    @Input() textEditorOpenedDialogs$ = new Observable<ITextEditorActiveButtons>();
    @Output() documentCommandExecute = new EventEmitter();

    @Output() insertMediaUsingFileManager = new EventEmitter();
    @Output() insertMediaUsingCamera = new EventEmitter();
    @Output() insertMediaUsingBrowseDevice = new EventEmitter();
    @Output() insertLinkAttachment = new EventEmitter<ITextEditorLinkAttachment>();
    @Output() insertLink = new EventEmitter<FiLinkToType>();
    @Output() insertEmbedVideo = new EventEmitter();
    @ViewChild('selectLinkToType', { static: true }) matMenuSelectLinkToType: MatMenu;

    selectedButtonsData: ISelectedButtonsData = {
        bold: false,
        underline: false,
        italic: false,
    };
    fiLinkToType = FiLinkToType;

    panelButtons: ITextEditorPanelButton[];
    isGiphyOverlayShown$ = new BehaviorSubject<boolean>(false);
    protected availableLinkToItems: ITextPanelMenuItem[];
    private textEditorOpenedDialogs: ITextEditorActiveButtons = {};

    constructor(
        private contenteditableService: FiContenteditableService,
        private linkToTypesService: LinkToTypesService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.textEditorOpenedDialogs$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((val: ITextEditorActiveButtons) => {
            this.textEditorOpenedDialogs = cloneDeep(val);
            this.cdr.markForCheck();
        });

        this.panelButtons = [
            {
                onClick: () => this.changeTextStyles('bold', false, null),
                isSelected: () => this.selectedButtonsData.bold,
                iconClass: 'fas fa-bold',
                titleTextId: 'text_editor.bold',
                buttonClass: 'text-style-button',
            },
            {
                onClick: () => this.changeTextStyles('underline', false, null),
                isSelected: () => this.selectedButtonsData.underline,
                iconClass: 'fas fa-underline',
                titleTextId: 'text_editor.underline',
                buttonClass: 'text-style-button',
            },
            {
                onClick: () => this.changeTextStyles('italic', false, null),
                isSelected: () => this.selectedButtonsData.italic,
                iconClass: 'fas fa-italic',
                titleTextId: 'text_editor.italic',
                buttonClass: 'text-style-button',
            },
            {
                onClick: () => {
                    document.execCommand('insertUnorderedList', false, null);
                    this.documentCommandExecute.emit();
                },
                isSelected: () =>
                    !!findClosest(this.contenteditableService.getRangeStartElement(), 'ul', '.text-editor'),
                iconClass: 'fas fa-list-ul',
                titleTextId: 'text_editor.bullet_list',
                buttonClass: 'list-button',
            },
            {
                onClick: () => {
                    document.execCommand('insertOrderedList', false, null);
                    this.documentCommandExecute.emit();
                },
                isSelected: () =>
                    !!findClosest(this.contenteditableService.getRangeStartElement(), 'ol', '.text-editor'),
                iconClass: 'fas fa-list-ol',
                titleTextId: 'text_editor.numbered_list',
                buttonClass: 'list-button',
            },
            {
                onClick: () => {
                    this.insertMediaUsingFileManager.emit();
                },
                isSelected: () => this.textEditorOpenedDialogs.attachments,
                iconClass: 'far fa-image',
                titleTextId: 'text_editor.insert_file',
                buttonClass: 'insert-file-button',
            },
            {
                onClick: () => {},
                isSelected: () => this.textEditorOpenedDialogs.link,
                iconClass: 'fas fa-link',
                titleTextId: 'text_editor.create_link',
                menuTriggerFor: this.matMenuSelectLinkToType,
                buttonClass: 'create-link-button',
            },
            {
                onClick: () => {
                    this.inputElementRef.nativeElement.focus();
                    document.execCommand('insertText', false, '@');
                    this.documentCommandExecute.emit();
                },
                iconClass: 'fas fa-user',
                titleTextId: 'text_editor.tag_user',
                buttonClass: 'tag-user-button',
            },
            {
                onClick: () => {
                    this.insertEmbedVideo.emit();
                },
                isSelected: () => this.textEditorOpenedDialogs.embed,
                iconClass: 'fa fa-video-camera',
                titleTextId: 'text_editor.insert_video_embded_code',
                buttonClass: 'insert-video-button',
            },
            {
                onClick: () => {
                    this.toggleGiphyOverlay(true);
                },
                isSelected: () => this.isGiphyOverlayShown$.value,
                iconClass: 'fas fa-film',
                titleTextId: '',
                buttonClass: 'insert-gif-button',
            },
        ];

        this.availableLinkToItems = this.linkToTypesService.getAvailableLinkToItems();
    }

    onGiphyItemClick($event: ITextEditorLinkAttachment) {
        this.toggleGiphyOverlay(false);
        this.insertLinkAttachment.emit($event);
    }

    toggleGiphyOverlay(isShown: boolean) {
        setTimeout(() => this.isGiphyOverlayShown$.next(isShown));
    }

    ngAfterViewInit(): void {
        fromEvent(this.inputElementRef.nativeElement, 'mouseup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(500)))
            .subscribe(() => this.setButtonsSelected());
        fromEvent(this.inputElementRef.nativeElement, 'keyup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .pipe(debounce(() => interval(500)))
            .subscribe(() => this.setButtonsSelected());
    }

    private setButtonsSelected() {
        const el = this.contenteditableService.getRangeStartElement();
        if (el) {
            this.contenteditableService.updateSelectedButtonsData(
                this.selectedButtonsData,
                el,
                false,
                '.fi-contenteditable',
            );
            this.cdr.markForCheck();
        }
    }

    changeTextStyles(fontStyleButton: string, showDefaultUI: boolean, valueArgument) {
        document.execCommand(fontStyleButton, showDefaultUI, valueArgument);
        this.selectedButtonsData[fontStyleButton] = !this.selectedButtonsData[fontStyleButton];
        this.documentCommandExecute.emit();
    }

    onInsertLinkClick(linkToType: any) {
        this.insertLink.emit(linkToType);
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
